// <tri-repeater> — add/remove repeating rows or fieldsets for Rails
// nested attributes: the "IPI holders" and "Songs" editors. Markup
// convention (rendered by Components::Ui::Repeater):
//
//   <tri-repeater>
//     <div data-repeater-rows>
//       <div data-repeater-row>… <button data-repeater-remove>🗑</button></div>
//     </div>
//     <template data-repeater-blueprint>
//       <div data-repeater-row>
//         <input name="catalogue[songs][__INDEX__][title]">
//         …
//       </div>
//     </template>
//     <button data-repeater-add>Add song</button>
//   </tri-repeater>
//
// New rows clone the blueprint with every __INDEX__ replaced by a
// timestamp, so indices never collide with the server-rendered rows'
// 0..n-1 — Rails' nested-attributes parser only needs keys to be unique,
// not sequential. Rows can be whole fieldsets: __INDEX__ is replaced
// everywhere in the blueprint, however many inputs it contains. Without
// JS the server-rendered rows still submit; only add/remove needs the
// element. Removal is DOM removal: CataloguesController's sync treats
// absent persisted rows as deletions.
//
// Emits bubbling `tri-repeater:add` / `tri-repeater:remove` CustomEvents
// (detail: { row }) after each mutation, for anything that needs to react
// — live counters, empty-state toggles, dirty-form tracking.

class TriRepeater extends HTMLElement {
	#lifetime: AbortController | null = null;

	connectedCallback() {
		this.#lifetime?.abort();
		this.#lifetime = new AbortController();

		this.addEventListener(
			"click",
			(event) => {
				const target = event.target;
				if (!(target instanceof Element)) return;

				if (target.closest("[data-repeater-add]")) {
					event.preventDefault();
					this.#addRow();
					return;
				}

				const remover = target.closest("[data-repeater-remove]");
				if (remover) {
					event.preventDefault();
					const row = remover.closest("[data-repeater-row]");
					if (row) {
						row.remove();
						this.#emit("remove", row);
					}
				}
			},
			{ signal: this.#lifetime.signal },
		);
	}

	disconnectedCallback() {
		this.#lifetime?.abort();
	}

	#addRow() {
		const blueprint = this.querySelector<HTMLTemplateElement>(
			"template[data-repeater-blueprint]",
		);
		const rows = this.querySelector<HTMLElement>("[data-repeater-rows]");
		if (!blueprint || !rows) return;

		const html = blueprint.innerHTML.replaceAll(
			"__INDEX__",
			Date.now().toString(),
		);
		rows.insertAdjacentHTML("beforeend", html);

		const row = rows.lastElementChild;
		if (!row) return;

		row.querySelector<HTMLElement>("input, textarea, select")?.focus();
		this.#emit("add", row);
	}

	#emit(kind: "add" | "remove", row: Element) {
		this.dispatchEvent(
			new CustomEvent(`tri-repeater:${kind}`, {
				bubbles: true,
				detail: { row },
			}),
		);
	}
}

if (!customElements.get("tri-repeater")) {
	customElements.define("tri-repeater", TriRepeater);
}
